import Accounting from "accounting";
import PropTypes from "prop-types";
import React from "react";
import { Button, Dropdown, FormControl, Glyphicon, MenuItem, OverlayTrigger, Tooltip } from "react-bootstrap";
import _ from "lodash";

import BudgetActions from "../actions/BudgetActions";

import { orderings } from "../constants/AppConstants";

import CategoryStore from "../stores/CategoryStore";
import CurrencyStore from "../stores/CurrencyStore";
import UserStore from "../stores/UserStore";

import AccountingHelper from "../utils/AccountingHelper";

import CategorySelect from "./CategorySelect";
import InputAmount from "./InputAmount";
import ModalWrapper from "./ModalWrapper";

const Enums = window.Enums;

export default class BudgetTemplateEdit extends React.Component
{
	constructor(props)
	{
		super(props);

		let budgetOrdering = orderings[0];
		const selectedOrderingValue = UserStore.getUserProfileSetting("budgetOrdering");
		if (selectedOrderingValue)
		{
			budgetOrdering = orderings.find( o => o.value === selectedOrderingValue);
		}

		const state = {
			allocations: [],
			automatic: true,
			description: "",
			frequency: Enums.frequency.unspecified,
			lastRun: null,
			dayOrMonth: "",
			currencyId: null,
			incomeCategoryId: null,
			incomeAllocationId: null,
			budgetOrdering,
			id: null
		};
		if (props.initialTemplate)
		{
			let allocations = _.sortBy([].concat(props.initialTemplate.allocations), "amount");
			let firstAllocation = allocations.shift();
			allocations = this.sortAllocations(budgetOrdering, allocations);
			state.allocations = allocations;
			if (firstAllocation)
			{
				state.incomeAllocationId = firstAllocation.id;
				state.incomeCategoryId = firstAllocation.categoryId;
			}
			state.automatic = props.initialTemplate.isAutomatic;
			state.description = props.initialTemplate.description;
			state.frequency = props.initialTemplate.frequency;
			state.lastRun = props.initialTemplate.lastRun;
			state.dayOrMonth = props.initialTemplate.dayOrMonth;
			state.currencyId = props.initialTemplate.currencyId;
			state.numberDecimals = AccountingHelper.getNumberDecimals(state.currencyId);
			state.id = props.initialTemplate.id;
		}
		if (UserStore.areMultipleCurrenciesEnabled())
		{
			state.areMultipleCurrenciesEnabled = true;
			state.currencies = CurrencyStore.getCurrencies();
		}
		this.state = state;
		this.handleAmountChange = this.handleAmountChange.bind(this);
		this.handleMaxAmountChange = this.handleMaxAmountChange.bind(this);
		this.handleCategoryChange = this.handleCategoryChange.bind(this);
		this.handleCategorySort = this.handleCategorySort.bind(this);
		this.handleCurrencyChange = this.handleCurrencyChange.bind(this);
		this.handleIncomeCategoryChange = this.handleIncomeCategoryChange.bind(this);
		this.handleAutomaticChange = this.handleAutomaticChange.bind(this);
		this.handleClose = this.handleClose.bind(this);
		this.handleDescriptionChange = this.handleDescriptionChange.bind(this);
		this.handleFrequencyChange = this.handleFrequencyChange.bind(this);
		this.handleDayOrMonthChange = this.handleDayOrMonthChange.bind(this);
		this.handleNewAllocation = this.handleNewAllocation.bind(this);
		this.handleAllocationDelete = this.handleAllocationDelete.bind(this);
		this.handleSave = this.handleSave.bind(this);
	}

	allocationHasCategoryId(allocation)
	{
		return !!allocation.categoryId;
	}

	getBudgetAmountForAllocation(budgetItems, allocation)
	{
		const budgetItem = budgetItems.find(budgetItem => budgetItem.categoryId === allocation.categoryId);
		if (!budgetItem)
		{
			return Number.MIN_SAFE_INTEGER;
		}
		return budgetItem.amount;
	}

	getBudgetSequenceForAllocation(budgetItems, allocation)
	{
		const budgetItem = budgetItems.find(budgetItem => budgetItem.categoryId === allocation.categoryId);
		if (!budgetItem)
		{
			return Number.MAX_SAFE_INTEGER;
		}
		return budgetItem.sequence;
	}

	getCategoryNameForAllocation(categories, allocation)
	{
		const category = categories.find(cat => cat.id === allocation.categoryId);
		if (!category)
		{
			return "";
		}
		return category.name;
	}

	sortAllocations(budgetOrdering, allocations)
	{
		let sortParts = budgetOrdering.value.split(":");

		switch (sortParts[0]) {
			case "amount":
				allocations = _.orderBy(allocations, [this.allocationHasCategoryId, this.getBudgetAmountForAllocation.bind(this, this.props.budgetItems)], ["desc", sortParts[1]]);
				break;
			case "name": {
				const categories = CategoryStore.getCategories();
				allocations = _.orderBy(allocations, [this.allocationHasCategoryId, this.getCategoryNameForAllocation.bind(this, categories)], ["desc", sortParts[1]]);
				break;
			}
			case "user":
				allocations = _.sortBy(allocations, this.getBudgetSequenceForAllocation.bind(this, this.props.budgetItems));
		}

		return allocations;
	}

	async handleSave()
	{
		const allocations = this.state.allocations;

		const incomeAllocation = {
			categoryId: this.state.incomeCategoryId,
			// TODO-US261 - need to handle toFixed(,2) should use currency.numDecimals
			amount: -(Accounting.toFixed(_.sumBy(allocations, allocation => {
				const value = +allocation.amount;
				return isNaN(value) ? 0 : value;
			}), 2))
		};
		const budgetTemplate = {
			description: this.state.description,
			isAutomatic: this.state.automatic,
			frequency: this.state.frequency,
			lastRun: this.state.lastRun,
			dayOrMonth: this.state.dayOrMonth,
			currencyId: this.state.currencyId
		};
		if (this.state.id)
		{
			budgetTemplate.id = this.state.id;
			incomeAllocation.budgetTemplateId = this.state.id;
			incomeAllocation.id = this.state.incomeAllocationId;
		}
		budgetTemplate.allocations = allocations.concat(incomeAllocation);

		try
		{
			await BudgetActions.saveBudgetTemplate(budgetTemplate);
			this.handleClose();
		}
		catch (err)
		{
			alert(err);
		}
	}

	handleClose()
	{
		this.props.onClose();
	}

	handleCategoryChange(allocation, index, category)
	{
		allocation.categoryId = category ? category.id : null;
		const allocations = this.state.allocations;
		allocations[index] = allocation;
		this.setState({allocations});
	}

	handleCategorySort(selectedValue)
	{
		const budgetOrdering = orderings.find(t => t.value === selectedValue);
		const allocations = this.sortAllocations(budgetOrdering, this.state.allocations);
		this.setState({budgetOrdering, allocations});
	}

	handleIncomeCategoryChange(category)
	{
		let categoryId = category ? category.id : null;
		this.setState({incomeCategoryId: categoryId});
	}

	handleAmountChange(allocation, index, value)
	{
		allocation.amount = value;
		const allocations = this.state.allocations;
		allocations[index] = allocation;
		this.setState({allocations});
	}

	handleMaxAmountChange(allocation, index, value)
	{
		allocation.maxAmount = value;
		const allocations = this.state.allocations;
		allocations[index] = allocation;
		this.setState({allocations});
	}

	handleAutomaticChange(event)
	{
		const automatic = event.currentTarget.checked;
		const state = {automatic};
		if (!automatic)
		{
			state.frequency = Enums.frequency.unspecified;
			state.dayOrMonth = "";
		}
		this.setState(state);
	}

	handleDescriptionChange(event)
	{
		this.setState({description: event.currentTarget.value});
	}

	handleCurrencyChange(event)
	{
		let currencyId = +event.target.value;
		if (currencyId === 0)
		{
			currencyId = null;
		}
		const numberDecimals = AccountingHelper.getNumberDecimals(currencyId);
		this.setState({ currencyId, numberDecimals });
	}

	handleFrequencyChange(event)
	{
		const stateToSet = {frequency: parseInt(event.currentTarget.value, 10)};
		if (stateToSet.frequency === Enums.frequency.monthly)
		{
			stateToSet.dayOrMonth = 1;
		}
		else if (stateToSet.frequency === Enums.frequency.annually)
		{
			stateToSet.dayOrMonth = Enums.month.january;
		}
		else
		{
			stateToSet.dayOrMonth = "";
		}
		this.setState(stateToSet);
	}

	handleDayOrMonthChange(event)
	{
		this.setState({dayOrMonth: event.currentTarget.value});
	}

	handleNewAllocation()
	{
		const allocations = this.state.allocations;
		allocations.push({amount: 0});
		this.setState({allocations});
	}

	handleAllocationDelete(index)
	{
		const allocations = this.state.allocations;
		allocations.splice(index, 1);
		this.setState({allocations});
	}

	render()
	{
		const categories = CategoryStore.getCategories();
		const {automatic, description, frequency, dayOrMonth, currencies, currencyId} = this.state;
		const incomeCategoryId = this.state.incomeCategoryId;
		const budgetOrdering = this.state.budgetOrdering;
		const budgetItems = this.props.budgetItems;
		let allocations = this.state.allocations;
		let incomeCategoryAmount = 0;
		if (incomeCategoryId)
		{
			let incomeBudgetItem = budgetItems.find(budgetItem => budgetItem.categoryId === incomeCategoryId && budgetItem.currencyId === currencyId);
			if (incomeBudgetItem)
			{
				incomeCategoryAmount = incomeBudgetItem.amount;
			}
		}

		const totalAmount = AccountingHelper.formatMoney(_.sumBy(allocations, allocation => {
			const value = +allocation.amount;
			return isNaN(value) ? 0 : value;
		}), currencyId);

		const footerElements = [
			<Button key={0} bsStyle={"primary"} onClick={this.handleSave}>Save</Button>,
			<Button key={1} onClick={this.handleClose}>Close</Button>
		];

		const showCurrencies = currencies && currencies.length > 0;

		return (
			<ModalWrapper
				title={"Budget Details"}
				modalClassName={"budget-modal"}
				footerElements={footerElements}
			>
				<div>
					<div className={"form-group row"}>
						<label className={"col-sm-4"}>
							Description
						</label>
						<div className={"col-sm-8"}>
							<FormControl
								type="text"
								className="form-control input-sm"
								placeholder="Description"
								value={description}
								onChange={this.handleDescriptionChange}
								autoFocus={true}
							/>
						</div>
					</div>
					<div className={"form-group row"}>
						<label className={"col-sm-4"}>
							Automatic
						</label>
						<div className={"col-sm-8"}>
							<label className="switch round small">
								<input type="checkbox" checked={automatic} onChange={this.handleAutomaticChange} />
								<span />
							</label>
						</div>
					</div>
					{automatic &&
						<div className={"form-group row"}>
							<label className={"col-sm-4"}>
								Frequency
							</label>
							<div className={"col-sm-8"}>
								<FormControl
									componentClass="select"
									value={frequency}
									onChange={this.handleFrequencyChange}
								>
									{Object.keys(Enums.frequency).map(frequency =>
									{
										let typeName = frequency[0].toUpperCase() + frequency.substring(1);
										if (typeName === "Unspecified")
										{
											typeName = "";
										}
										const typeId = Enums.frequency[frequency];

										return (
											<option key={typeId} value={typeId}>{typeName}</option>
										);
									})}
								</FormControl>
							</div>
						</div>
					}
					{frequency === Enums.frequency.annually &&
						<div className={"form-group row"}>
							<label className={"col-sm-4"}>
								Month
							</label>
							<div className={"col-sm-8"}>
								<FormControl
									componentClass="select"
									value={dayOrMonth}
									onChange={this.handleDayOrMonthChange}
								>
									{Object.keys(Enums.month).map(month =>
									{
										let typeName = month[0].toUpperCase() + month.substring(1);
										if (typeName === "Unspecified")
										{
											typeName = "";
										}
										const typeId = Enums.month[month];

										return (
											<option key={typeId} value={typeId}>{typeName}</option>
										);
									})}
								</FormControl>
							</div>
						</div>
					}
					{frequency === Enums.frequency.monthly &&
						<div className={"form-group row"}>
							<label className={"col-sm-4"}>
								Day
							</label>
							<div className={"col-sm-8"}>
								<FormControl
									componentClass="select"
									value={dayOrMonth}
									onChange={this.handleDayOrMonthChange}
								>
									{_.range(1, 31).map(day =>
									{
										return ( <option key={day} value={day}>{day}</option> );
									})}
								</FormControl>
							</div>
						</div>
					}
					{showCurrencies &&
						<div className={"form-group row"}>
							<label className={"col-sm-4"}>
								Currency
							</label>
							<div className={"col-sm-8"}>
								<FormControl
									id="moveMoneyCurrency"
									componentClass="select"
									placeholder="Currency"
									className="mousetrap-mod"
									onChange={this.handleCurrencyChange}
									value={currencyId || ""}
								>
									<option></option>
									{currencies.map(currency => {
										return (
											<option key={currency.id} value={currency.id}>
												{currency.name}
											</option>
										);
									})}
								</FormControl>
							</div>
						</div>
					}
					<div className={"form-group row"}>
						<label className={"col-sm-4"}>
							From Category
						</label>
						<div className={"col-sm-8"}>
							<CategorySelect
								onSelect={this.handleIncomeCategoryChange}
								value={incomeCategoryId}
								options={categories}
								budgetItems={this.props.budgetItems}
								currencyId={currencyId}
							/>
							{incomeCategoryId &&
								<span className="label label-info category-amount">{AccountingHelper.formatMoney(incomeCategoryAmount, currencyId)}</span>
							}
						</div>
					</div>
					<div className={"form-group row"}>
						<div className={"col-sm-4"}>
							<h4>
								Category
								<Dropdown
									bsSize={"xs"}
									bsStyle={"default"}
									className={"category-sort-button"}
									title={budgetOrdering.displayName}
									onSelect={this.handleCategorySort}
									id={"dropdown-category-sort"}
								>
									<Dropdown.Toggle noCaret>
										<Glyphicon glyph={"sort"}/>
									</Dropdown.Toggle>
									<Dropdown.Menu>
										{orderings.map(ordering => {
											return (
												<MenuItem
													key={ordering.value}
													eventKey={ordering.value}
													className={ordering.value === budgetOrdering.value ? "active" : ""}
												>
													{ordering.displayName}
												</MenuItem>
											);
										})}
									</Dropdown.Menu>
								</Dropdown>
							</h4>
						</div>
						<h4 className={"col-sm-3"}>
							Amount
						</h4>
						<h4 className={"col-sm-3"}>
							Max&nbsp;
							<OverlayTrigger placement="bottom" overlay={<Tooltip><div>When the budget is run, the amount put into the category will be adjusted so that it will not exceed this amount. If the category already has over this amount allocated the nothing will be allocated when the budget is run.</div></Tooltip>}>
								<Glyphicon glyph="info-sign" />
							</OverlayTrigger>
						</h4>
						<div className={"col-sm-2"} />
					</div>
					{allocations.map((allocation, index) => {
						let allocationBudgetItem = this.props.budgetItems.find(budgetItem => budgetItem.categoryId === allocation.categoryId && budgetItem.currencyId === currencyId);
						let amount = 0;
						if (allocationBudgetItem) {
							amount = allocationBudgetItem.amount;
						}

						return (
							<div key={index} className={"form-group row"}>
								<div className={"col-sm-4"}>
									<CategorySelect
										onSelect={this.handleCategoryChange.bind(this, allocation, index)}
										value={allocation.categoryId}
										options={categories}
										autoFocus={true}
										budgetItems={this.props.budgetItems}
										currencyId={currencyId}
									/>
									<span className="label label-info category-amount">{AccountingHelper.formatMoney(amount, currencyId)}</span>
								</div>
								<div className={"col-sm-3"}>
									<InputAmount
										pullRight={false}
										value={allocation.amount}
										onChange={this.handleAmountChange.bind(this, allocation, index)}
										numberDecimals={this.state.numberDecimals}
									/>
								</div>
								<div className={"col-sm-3"}>
									<InputAmount
										pullRight={false}
										value={allocation.maxAmount}
										onChange={this.handleMaxAmountChange.bind(this, allocation, index)}
										numberDecimals={this.state.numberDecimals}
										allowNull={true}
									/>
								</div>
								<div className={"col-sm-2"}>
									<Button
										bsSize={"sm"}
										bsStyle={"danger"}
										onClick={this.handleAllocationDelete.bind(this, index)}
									>
										<Glyphicon glyph={"trash"}/>
									</Button>
								</div>
							</div>
						);
					})}
					<div className={"form-group row"}>
						<div className={"col-sm-5"}>
							<Button bsStyle={"link"} onClick={this.handleNewAllocation}>Add new</Button>
						</div>
						<h4 className={"col-sm-5"}>
							Total Allocated: {totalAmount}
						</h4>
						<div className={"col-sm-2"} />
					</div>
				</div>
			</ModalWrapper>
		);
	}
}

BudgetTemplateEdit.propTypes = {
	onClose: PropTypes.func,
	budgetItems: PropTypes.arrayOf(
		PropTypes.shape({
			amount: PropTypes.number,
			categoryId: PropTypes.number
		})
	),
	initialTemplate: PropTypes.shape({
		allocations: PropTypes.arrayOf(
			PropTypes.shape({
				amount: PropTypes.number,
				categoryId: PropTypes.number,
				budgetTemplateId: PropTypes.number
			})
		),
		isAutomatic: PropTypes.bool,
		description: PropTypes.string,
		frequency: PropTypes.number,
		dayOrMonth: PropTypes.number,
		currencyId: PropTypes.number,
		lastRun: PropTypes.string,
		id: PropTypes.number
	})
};



// WEBPACK FOOTER //
// ./Components/components/BudgetTemplateEdit.jsx